import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { buttonBase, buttonRecipe, loadingSpinner } from './Button.css';

/**
 * Button variant. 선택은 페이지 모티프 × 요소 Tier 의 함수다 — DESIGN.md §Atom Standards 1-1.
 *
 * TIER 1(그 화면의 주역)은 페이지 모티프를 따른다:
 *   primary·secondary·danger·success = 글래스(기본 모티프)
 *   chip·flat = 운영자 콘솔 절제 모티프 — chip 은 토글(눌림은 aria-pressed 로), flat 은 제출
 * TIER 2·3(목록 행·인라인 액션·보조 컨트롤)은 모티프와 무관하게 중립 표면을 쓴다:
 *   ghost = 투명 + 무테(조용한 보조 액션), link = 파란 글자 + hover 밑줄(인라인 텍스트 액션),
 *   outline = 투명 + 실선 hairline, soft = 채움 + hairline
 * 이름은 업계 관례(Chakra solid·outline·ghost·link)를 따른다 — 2026-09-01 ghost 의 점선을 걷고
 * text 를 link 로 개명했다. 표면 축 표는 DESIGN.md §Atom Standards 1-1.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'success'
  | 'chip'
  | 'flat'
  | 'link'
  | 'outline'
  | 'soft';

/** Button size. 기본은 `sm`, `xs` 는 목록·툴바 밀도(6px 14px). */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Button tone. 표면 축과 직교하는 의미 축이다 — DESIGN.md §Atom Standards 1-1.
 *
 * 톤을 받는 표면은 `link`·`outline`·`flat`·`soft` 넷뿐이다. 글래스 5종은 `danger`·`success` 를
 * 이미 variant 로 갖고 있어 톤까지 열면 같은 뜻이 두 축에 앉고, `chip` 은 실측 수요가 0 이다.
 * 그 조합에 톤을 주면 타입은 통과하지만 아무 색도 붙지 않는다 —
 * `variant={confirmVariant}` 같은 동적 호출부(실측 9곳)가 있어 타입 유니온으로 막으면
 * 호출부가 깨지므로, 금지는 `Button.tone.test.tsx` 의 compound 가드가 든다.
 *
 * `data-tone` 은 `default` 일 때 렌더하지 않는다 — 기존 465건의 DOM 을 그대로 두기 위해서다.
 */
export type ButtonTone = 'default' | 'danger' | 'warning';

/** Button props */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Ref */
  ref?: Ref<HTMLButtonElement>;
  /** Button variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Button tone — `link`·`outline`·`flat`·`soft` 에서만 색이 붙는다 */
  tone?: ButtonTone;
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Left icon */
  leftIcon?: ReactNode;
  /** Right icon */
  rightIcon?: ReactNode;
  /** Custom class name */
  className?: string;
  /** Children */
  children?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'sm',
  tone = 'default',
  fullWidth = false,
  loading = false,
  disabled = false,
  // HTML 기본값은 submit 이라, form 안에 놓인 Button 이 표시 없이 폼을 제출한다.
  // 제출이 필요하면 호출부가 type="submit" 을 명시한다.
  type = 'button',
  leftIcon,
  rightIcon,
  children,
  className,
  style,
  ref,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  const recipeClass = buttonRecipe({
    variant,
    size,
    tone,
    fullWidth,
    isDisabled,
  });

  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      data-tone={tone === 'default' ? undefined : tone}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={`${buttonBase} ${recipeClass}${className ? ` ${className}` : ''}`}
      style={style}
      {...props}
    >
      {loading && <span className={loadingSpinner} />}
      {!loading && leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
